import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from "react";
import { useUser } from "@clerk/react";
import { ChevronLeft, ChevronRight, Waves, Wind } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { apiRequest } from "@/lib/queryClient";
import { getForecastSnapshots } from "@/lib/forecast-presentation";
import { useForecastDateRollover } from "@/hooks/use-forecast-date-rollover";
import { calendarDay, localDate } from "@shared/forecast";
import type { ForecastDay, Location } from "@/types/weather";
import DetailedForecastModal from "./DetailedForecastModal";
import TideChart from "./TideChart";

interface ForecastSectionProps {
  location: Location;
}

// Bound to cache-entry lifetime, not component lifetime: failed date refreshes
// must not loop when a user revisits a spot or switches away and back.
const rolloverAttempts = new WeakMap<object, string>();

export default function ForecastSection({ location }: ForecastSectionProps) {
  const queryClient = useQueryClient();
  const { user, isLoaded: authLoaded } = useUser();
  const owner = user?.id ?? "anonymous";
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const activeIndexRef = useRef(0);
  const [visibleCount, setVisibleCount] = useState(1);
  const [detailState, setDetailState] = useState<{ owner: string; locationId: number; day: ForecastDay } | null>(null);
  const setDetailDay = (day: ForecastDay | null) =>
    setDetailState(day ? { owner, locationId: location.id, day } : null);

  const queryKey = [`/api/locations/${location.id}/forecast`, owner, "hourly-cards-v2"];
  const { data: cachedForecast = [], isLoading: forecastLoading, error, refetch } = useQuery<ForecastDay[]>({
    // The new cards require hourly values; do not reuse an older daily-only
    // response after a live frontend update.
    queryKey,
    // The default query function joins key parts into a URL; use an explicit
    // URL while keeping licensed cache entries scoped to their requesting user.
    queryFn: async () => (await apiRequest(`/api/locations/${location.id}/forecast`, { method: "GET" })).json(),
    enabled: authLoaded,
    staleTime: 30 * 60 * 1000,
  });
  const timezone = cachedForecast.find(day => day.timezone)?.timezone;
  const cachedBaseDate = cachedForecast[0]
    ? calendarDay(cachedForecast[0].dateKey, -cachedForecast[0].dayOffset) : undefined;
  const localDay = useForecastDateRollover(
    `${location.id}:${owner}`, timezone, authLoaded,
    () => {
      setDetailState(null);
      const query = queryClient.getQueryCache().find({ queryKey, exact: true });
      const date = timezone ? localDate(Date.now(), timezone) : null;
      if (!query || !date || rolloverAttempts.get(query) === date) return;
      rolloverAttempts.set(query, date);
      // Reuse the authenticated query and server-side provider cache/budgets.
      // Errors remain visible through the query's existing recoverable error UI.
      // Reuse an in-flight normal mount fetch rather than submitting a duplicate.
      void refetch({ cancelRefetch: false }).catch(() => {});
    },
    cachedBaseDate,
  );
  // Never relabel stale provider data as Tomorrow or reuse its relative hourly offset.
  const forecast = cachedForecast.filter(day =>
    !localDay || calendarDay(day.dateKey, -day.dayOffset) === localDay);
  const datesChanged = cachedForecast.length > 0 && forecast.length === 0;
  const detailDay = authLoaded && detailState?.owner === owner &&
    detailState.locationId === location.id && forecast.includes(detailState.day)
    ? detailState.day : null;
  const isLoading = !authLoaded || forecastLoading;

  const total = isLoading ? 5 : forecast.length;
  const hasForecast = !isLoading && forecast.length > 0;
  const lastStartIndex = Math.max(0, total - visibleCount);

  useEffect(() => {
    activeIndexRef.current = 0;
    setActiveIndex(0);
    setDetailState(null);
    scrollContainerRef.current?.scrollTo?.({ left: 0 });
  }, [location.id, owner, forecast[0]?.dateKey]);

  const alignToIndex = useCallback((index: number) => {
    const el = scrollContainerRef.current;
    const card = el?.children[index] as HTMLElement | undefined;
    if (!el || !card) return;
    const left = card.getBoundingClientRect().left - el.getBoundingClientRect().left + el.scrollLeft;
    el.scrollTo({ left });
  }, []);

  const measureViewport = useCallback(() => {
    const el = scrollContainerRef.current;
    const firstCard = el?.firstElementChild as HTMLElement | null;
    const cardWidth = firstCard?.getBoundingClientRect().width ?? 0;
    if (!el || !firstCard || !cardWidth) return;
    const styles = window.getComputedStyle(el);
    const gap = Number.parseFloat(styles.columnGap || styles.gap || "0") || 0;
    // Count only fully visible cards. The fractional rect width avoids integer
    // offsetWidth rounding down a two-card desktop viewport.
    const count = Math.max(1, Math.floor((el.clientWidth + gap + 0.5) / (cardWidth + gap)));
    const boundedIndex = Math.min(activeIndexRef.current, Math.max(0, total - count));
    if (boundedIndex !== activeIndexRef.current) {
      activeIndexRef.current = boundedIndex;
      setActiveIndex(boundedIndex);
    }
    setVisibleCount(current => current === count ? current : count);
    // A resized viewport can retain the old pixel offset, which may land
    // between days even if the leading day remains within the new bounds.
    alignToIndex(boundedIndex);
  }, [alignToIndex, total]);

  useEffect(() => {
    measureViewport();
    const el = scrollContainerRef.current;
    if (!el) return;
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measureViewport);
    observer?.observe(el);
    window.addEventListener("resize", measureViewport);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", measureViewport);
    };
  }, [measureViewport, isLoading, forecast.length, !!error]);

  const scrollTo = useCallback((index: number) => {
    if (!hasForecast) return;
    const el = scrollContainerRef.current;
    if (!el) return;
    const card = el.children[Math.min(Math.max(index, 0), lastStartIndex)] as HTMLElement;
    if (!card) return;
    const left = card.getBoundingClientRect().left - el.getBoundingClientRect().left + el.scrollLeft;
    el.scrollTo({ left, behavior: "smooth" });
    const boundedIndex = Math.min(Math.max(index, 0), lastStartIndex);
    activeIndexRef.current = boundedIndex;
    setActiveIndex(boundedIndex);
  }, [hasForecast, lastStartIndex]);

  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!hasForecast || !el || !el.children.length) return;
    const firstCard = el.children[0] as HTMLElement;
    const styles = window.getComputedStyle(el);
    const gap = Number.parseFloat(styles.columnGap || styles.gap || "0") || 0;
    const idx = Math.round(el.scrollLeft / (firstCard.getBoundingClientRect().width + gap));
    const boundedIndex = Math.max(0, Math.min(idx, lastStartIndex));
    activeIndexRef.current = boundedIndex;
    setActiveIndex(boundedIndex);
  }, [hasForecast, lastStartIndex]);

  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    if (!hasForecast) return;
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      scrollTo(activeIndex - 1);
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      scrollTo(activeIndex + 1);
    } else if (event.key === "Home") {
      event.preventDefault();
      scrollTo(0);
    } else if (event.key === "End") {
      event.preventDefault();
      scrollTo(lastStartIndex);
    }
  }, [activeIndex, hasForecast, lastStartIndex, scrollTo]);

  if (error) {
    return (
      <div className="w-full">
        <div className="w-full border-b border-white/[0.08] mt-8 mb-4" />
        <div className="container mx-auto px-4 md:px-6 max-w-7xl pb-4 mb-6">
          <div className="rounded-2xl border border-white/[0.08] bg-[#091a35] px-5 py-6 text-center">
            <p className="text-sm text-slate-300">Unable to load forecast data.</p>
            <button type="button" onClick={() => refetch()}
              className="mt-3 rounded-lg border border-emerald-400/25 px-3 py-1.5 text-xs font-semibold text-emerald-300 hover:bg-emerald-400/10">
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isToday = (d: ForecastDay) =>
    d.date.toLowerCase() === "today" || d.date.toLowerCase().startsWith("today");

  return (
    <div className="w-full">
      <div className="w-full border-b border-white/[0.08] mt-8 mb-4" />
      <div className="container mx-auto px-4 md:px-6 lg:px-0 max-w-7xl pb-4 mb-6">

        {/* ── Section header ── */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="font-bold tracking-widest uppercase text-[#ffffff] text-[14px]">5-day Forecast</span>
          </div>
          {/* Mobile day-by-day controls */}
          <div className="flex items-center gap-2 lg:hidden">
            <button
              type="button"
              onClick={() => scrollTo(activeIndex - 1)}
              disabled={!hasForecast || activeIndex === 0}
              className="w-6 h-6 rounded-full flex items-center justify-center transition-all disabled:opacity-20"
              style={{ background: "rgba(16,185,129,0.12)", border: "1px solid rgba(16,185,129,0.2)" }}
              aria-label="Previous day"
            >
              <ChevronLeft size={13} className="text-emerald-400" />
            </button>
            <button
              type="button"
              onClick={() => scrollTo(activeIndex + 1)}
              disabled={!hasForecast || activeIndex >= lastStartIndex}
              className="w-6 h-6 rounded-full flex items-center justify-center transition-all disabled:opacity-20"
              style={{ background: "rgba(16,185,129,0.12)", border: "1px solid rgba(16,185,129,0.2)" }}
              aria-label="Next day"
            >
              <ChevronRight size={13} className="text-emerald-400" />
            </button>
          </div>
          {/* Desktop advances one card at a time while keeping two in view. */}
          <div className="hidden items-center gap-3 lg:flex">
            {!isLoading && forecast.length > 0 && (
              <span className="min-w-[3.5rem] text-right font-mono text-[11px] tabular-nums text-slate-400" aria-live="polite">
                {activeIndex + 1}–{Math.min(activeIndex + visibleCount, total)} / {total}
              </span>
            )}
            <button
              type="button"
              onClick={() => scrollTo(activeIndex - 1)}
              disabled={!hasForecast || activeIndex === 0}
              className="flex h-8 w-8 items-center justify-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300"
              style={{ background: "rgba(16,185,129,0.12)", border: "1px solid rgba(16,185,129,0.2)" }}
              aria-label="Previous forecast days"
            >
              <ChevronLeft size={15} className="text-emerald-400" />
            </button>
            <button
              type="button"
              onClick={() => scrollTo(activeIndex + 1)}
              disabled={!hasForecast || activeIndex >= lastStartIndex}
              className="flex h-8 w-8 items-center justify-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300"
              style={{ background: "rgba(16,185,129,0.12)", border: "1px solid rgba(16,185,129,0.2)" }}
              aria-label="Next forecast days"
            >
              <ChevronRight size={15} className="text-emerald-400" />
            </button>
          </div>
        </div>

        {/* ── Cards ── */}
        <div
          ref={scrollContainerRef}
          onScroll={handleScroll}
          onKeyDown={handleKeyDown}
          tabIndex={hasForecast ? 0 : -1}
          role="region"
          aria-label="Five-day forecast cards"
          aria-roledescription="carousel"
          className={`flex gap-3 ${hasForecast ? "overflow-x-auto" : "overflow-hidden"} pb-2 snap-x snap-mandatory focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[#091a35] lg:overscroll-x-contain`}
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          {isLoading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex-shrink-0 lg:!w-[calc((100%_-_0.75rem)/2)] rounded-2xl overflow-hidden snap-start"
                style={{
                  width: "min(320px, calc(100vw - 2rem))",
                  background: "linear-gradient(160deg, #030912 0%, #091a35 100%)",
                  border: "1px solid rgba(255,255,255,0.08)",
                }}>
                <div className="px-4 pt-4 pb-3 border-b border-white/5 flex items-center justify-between">
                  <Skeleton className="h-4 w-16 bg-white/10" />
                  <Skeleton className="h-7 w-7 rounded-full bg-white/10" />
                </div>
                <div className="px-4 pt-3 pb-4">
                  <Skeleton className="h-3 w-24 bg-white/10 mb-3" />
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Skeleton className="h-3 w-12 bg-white/10" />
                      {[1, 2, 3].map(row => <Skeleton key={row} className="h-4 w-full bg-white/[0.06]" />)}
                    </div>
                    <div className="space-y-2">
                      <Skeleton className="h-3 w-12 bg-white/10" />
                      {[1, 2, 3].map(row => <Skeleton key={row} className="h-4 w-full bg-white/[0.06]" />)}
                    </div>
                  </div>
                </div>
                <div className="px-4 py-3 border-t border-white/5">
                  <Skeleton className="h-9 w-full rounded-xl bg-white/5" />
                </div>
              </div>
            ))
          ) : forecast.length > 0 ? (
            forecast.map((day, i) => {
              const today = isToday(day);
              const snapshots = getForecastSnapshots(day);
              const renderSnapshots = (kind: "swell" | "wind") => (
                <section aria-label={kind === "swell" ? "Swell forecast" : "Wind forecast"} className="min-w-0">
                  <div className="mb-1.5 flex items-center gap-1.5 text-white">
                    {kind === "swell" ? <Waves size={12} aria-hidden="true" /> : <Wind size={12} aria-hidden="true" />}
                    <h3 className="font-semibold uppercase tracking-[0.12em] text-[12px]">{kind === "swell" ? "Swell" : "Wind"}</h3>
                  </div>
                  <div className="space-y-1">
                    {snapshots.map(snapshot => (
                      <div key={`${kind}-${snapshot.label}`} className="grid grid-cols-[3.25rem_minmax(0,1fr)] items-baseline gap-2 border-b border-white/[0.045] py-1 last:border-0">
                        <span className="text-slate-400 text-[11px] font-light">{snapshot.label}</span>
                        <span className={`min-w-0 break-words font-medium text-[14px] ${kind === "swell" ? "text-emerald-300" : "text-cyan-300"}`}>
                          {snapshot[kind]}
                        </span>
                      </div>
                    ))}
                  </div>
                </section>
              );
              return (
                <div
                  key={day.dateKey}
                  className="flex-shrink-0 lg:!w-[calc((100%_-_0.75rem)/2)] rounded-2xl overflow-hidden flex flex-col snap-start ls-card-hover"
                  role="group"
                  aria-roledescription="slide"
                  aria-label={`${day.date}, forecast day ${i + 1} of ${forecast.length}`}
                  style={{
                    width: "min(320px, calc(100vw - 2rem))",
                    background: "linear-gradient(160deg, #030912 0%, #091a35 100%)",
                    border: today
                      ? "1px solid rgba(52,211,153,0.38)"
                      : "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  {/* Day header */}
                  <div className="px-4 pt-3 pb-2.5 border-b border-white/5 flex items-center justify-between">
                    <span className={`text-sm font-bold ${today ? "text-emerald-400" : "text-slate-200"}`}>
                      {day.date}
                    </span>
                  </div>

                  <div className="flex flex-1 flex-col px-4 pt-3 pb-4">
                    <div className="space-y-4">
                      {renderSnapshots("swell")}
                      {renderSnapshots("wind")}
                    </div>
                    <button
                      type="button"
                      onClick={() => setDetailDay(day)}
                      className="mt-4 w-full rounded-xl px-3 py-2.5 text-[11px] font-semibold text-sky-300 transition-colors hover:bg-sky-300/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-300"
                      style={{ border: "1px solid rgba(56,189,248,0.18)" }}
                    >
                      View hourly model values
                    </button>

                    <section className="mt-5 border-t border-white/[0.07] pt-4" aria-label="Tides">
                      <h3 className="mb-3 text-xs font-semibold uppercase tracking-[0.14em] text-slate-200">Tides</h3>
                      {day.tides?.length ? (
                        <>
                          <TideChart tides={day.tides} date={day.date} location={location} sunrise={day.sunrise} sunset={day.sunset} />
                          <div className="mt-2 grid grid-cols-2 gap-2 text-[10px]" aria-label="Sunrise and sunset">
                            <div className="flex items-center justify-between rounded-lg border border-white/[0.05] bg-white/[0.025] px-2.5 py-1.5">
                              <span className="text-amber-200/70">Sunrise</span>
                              <span className="font-mono tabular-nums text-slate-300">{day.sunrise || "Unavailable"}</span>
                            </div>
                            <div className="flex items-center justify-between rounded-lg border border-white/[0.05] bg-white/[0.025] px-2.5 py-1.5">
                              <span className="text-orange-200/70">Sunset</span>
                              <span className="font-mono tabular-nums text-slate-300">{day.sunset || "Unavailable"}</span>
                            </div>
                          </div>
                          <div className="mt-3 grid grid-cols-2 gap-2">
                            {day.tides.map((tide, tideIndex) => {
                              const isHighTide = tide.type === "high";
                              return (
                                <div key={`${tide.type}-${tide.time}-${tideIndex}`} className="rounded-lg bg-white/[0.035] border border-white/[0.05] px-2.5 py-2">
                                  <p className={`text-[9px] uppercase tracking-wider font-semibold ${isHighTide ? "text-emerald-300" : "text-sky-300"}`}>
                                    {isHighTide ? "High tide" : "Low tide"}
                                  </p>
                                  <p className="mt-0.5 text-[11px] text-slate-200">{tide.time}</p>
                                  <p className="text-[10px] text-slate-500">{tide.height.toFixed(1)} ft</p>
                                </div>
                              );
                            })}
                          </div>
                        </>
                      ) : (
                        <p className="rounded-xl border border-white/[0.05] bg-white/[0.025] px-3 py-3 text-[11px] text-slate-500">
                          {day.tideUnavailableReason === "no_ocean_tides"
                            ? "This freshwater location does not have ocean tide predictions."
                            : day.tideUnavailableReason === "sign_in_required"
                            ? "Sign in to check astronomical model tide predictions for this location."
                            : day.tideUnavailableReason === "no_station_mapping"
                            ? "No verified tide station is configured for this location."
                            : day.tideUnavailableReason === "provider_unavailable"
                            ? "Tide predictions are temporarily unavailable."
                            : "Tide details are unavailable for this day."}
                        </p>
                      )}
                      {day.tideSource === "WorldTides" && (
                        <div className="mt-3 space-y-2 text-[10px] leading-relaxed text-slate-500">
                          {day.tideAttribution && <p className="whitespace-pre-line">{day.tideAttribution}</p>}
                          <a href={day.tideAttributionUrl ?? "https://www.worldtides.info/copyright"} target="_blank" rel="noopener noreferrer"
                            className="inline-block text-emerald-300 underline underline-offset-2">WorldTides data sources and copyright</a>
                        </div>
                      )}
                    </section>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-slate-500 text-sm py-6">
              <p>{datesChanged ? "Forecast dates have changed. Waiting for updated forecast data." : "No forecast data available"}</p>
              {datesChanged && <button type="button" onClick={() => refetch()}
                className="mt-2 text-emerald-300 underline">Try again</button>}
            </div>
          )}
        </div>

        {/* ── Dot indicators ── */}
        {!isLoading && forecast.length > 0 && (
          <div className="flex items-center justify-center gap-1.5 mt-3 lg:hidden">
            {forecast.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => scrollTo(i)}
                className="transition-all rounded-full"
                style={{
                  width: i === activeIndex ? 16 : 6,
                  height: 6,
                  background: i === activeIndex
                    ? "#10b981"
                    : "rgba(255,255,255,0.12)",
                }}
                aria-label={`Go to ${forecast[i].date}`}
              />
            ))}
          </div>
        )}

        {!isLoading && forecast.length > 0 && <p className="text-slate-600 text-[9px] mt-3 text-center">
          Model forecast values · not buoy observations · no confidence percentages
        </p>}
        {detailDay && (
          <DetailedForecastModal
            open={!!detailDay}
            onOpenChange={open => { if (!open) setDetailDay(null); }}
            location={location}
            day={detailDay}
          />
        )}
      </div>
    </div>
  );
}
